import React from 'react';
import ReactDOM from 'react-dom/client';
import { Auth0Provider, Auth0Context } from '@auth0/auth0-react';
import './styles/tokens.css';
import './index.css';
import App from './App';
import SharedValuation from './pages/SharedValuation';
import reportWebVitals from './reportWebVitals';
import { calcAuth0ClientId, calcAuth0Domain } from './calcConfig';
import { calcAuth0AuthorizationParams } from './auth0Params';
import { defaultAuthedPath } from './routes';

// REACT_APP_AUTH_MOCK bypasses Auth0 entirely for local frontend dev.
// Pairs with CALCULATOR_INSECURE_SKIP_JWT=1 on the Go server (any non-empty
// Bearer token is accepted). Never set in any deployed environment.
//   "1"      -> mock authenticated user (drops you on the dashboard).
//   "unauth" -> mock unauthenticated user (lets you preview Landing /
//               SignIn / SignUp / Methodology without an Auth0 tenant).
const authMockRaw = String(process.env.REACT_APP_AUTH_MOCK || '').trim();
const authMock = authMockRaw === '1';
const authMockUnauth = authMockRaw === 'unauth';

const domain = calcAuth0Domain();
const clientId = calcAuth0ClientId();

const root = ReactDOM.createRoot(document.getElementById('root'));

// Public share-link route — render the read-only view without the
// authenticated app shell, Auth0 provider, or any nav. The path is
// `/share/<token>` and the token may contain dots (HMAC payload + sig)
// so we capture everything after the prefix.
const sharePath = '/share/';
if (typeof window !== 'undefined' && window.location.pathname.startsWith(sharePath)) {
  const token = decodeURIComponent(window.location.pathname.slice(sharePath.length));
  root.render(
    <React.StrictMode>
      <SharedValuation token={token} />
    </React.StrictMode>,
  );
} else if (authMock) {
  // Stub Auth0 context for local dev. Provides the surface that components
  // consuming useAuth0() expect (isAuthenticated, loginWithRedirect, logout,
  // getAccessTokenSilently, user, isLoading) so the app renders the
  // signed-in path without an Auth0 tenant.
  const mockAuthValue = {
    isAuthenticated: true,
    isLoading: false,
    user: {
      sub: 'dev-insecure',
      name: 'Dev User',
      email: 'dev@example.com',
      email_verified: true,
    },
    error: undefined,
    loginWithRedirect: async () => {},
    logout: async () => {},
    getAccessTokenSilently: async () => 'dev-insecure',
    getAccessTokenWithPopup: async () => 'dev-insecure',
    getIdTokenClaims: async () => ({ __raw: '', sub: 'dev-insecure' }),
    loginWithPopup: async () => {},
    handleRedirectCallback: async () => ({}),
  };
  root.render(
    <React.StrictMode>
      <Auth0Context.Provider value={mockAuthValue}>
        <App />
      </Auth0Context.Provider>
    </React.StrictMode>,
  );
} else if (authMockUnauth) {
  // Mocked unauthenticated context — lets you preview Landing, SignIn,
  // SignUp, and the public Methodology page locally without an Auth0
  // tenant. loginWithRedirect just toasts so the SSO buttons are inert
  // but don't error.
  const mockAuthValue = {
    isAuthenticated: false,
    isLoading: false,
    user: undefined,
    error: undefined,
    loginWithRedirect: async () => { window.alert('Sign-in is mocked. Run `REACT_APP_AUTH_MOCK=1 npm start` to enter the app.'); },
    logout: async () => {},
    getAccessTokenSilently: async () => '',
    getAccessTokenWithPopup: async () => '',
    getIdTokenClaims: async () => undefined,
    loginWithPopup: async () => {},
    handleRedirectCallback: async () => ({}),
  };
  root.render(
    <React.StrictMode>
      <Auth0Context.Provider value={mockAuthValue}>
        <App />
      </Auth0Context.Provider>
    </React.StrictMode>,
  );
} else if (!domain || !clientId) {
  root.render(
    <div style={{ padding: 24, fontFamily: 'system-ui', maxWidth: 560 }}>
      <h1 style={{ fontSize: 20 }}>Healthcare calculator</h1>
      <p>
        Configure Auth0 (runtime <code>/config.js</code> on ECS, or <code>REACT_APP_AUTH0_*</code> for local). Use the
        same tenant as the Loopora portal. For mock auth (local frontend dev without Auth0), set{' '}
        <code>REACT_APP_AUTH_MOCK=1</code> in <code>.env.local</code>.
      </p>
      <p style={{ color: '#64748b', fontSize: 14 }}>See docs/FRONTEND_BACKEND.md.</p>
    </div>,
  );
} else {
  // Auth0 SDK caches OIDC tokens in localStorage (identity only — not profile/consent).
  // Product data is server/Postgres; see docs/STORAGE_AND_SESSION.md for BFF/session direction.
  root.render(
    <React.StrictMode>
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        cacheLocation="localstorage"
        authorizationParams={calcAuth0AuthorizationParams()}
        onRedirectCallback={() => {
          window.history.replaceState({}, document.title, defaultAuthedPath());
        }}
      >
        <App />
      </Auth0Provider>
    </React.StrictMode>,
  );
}

reportWebVitals();
